/**
 * La conferma quando dei codici sono già usati (CF09) — CL06.
 *
 * `prepare` riserva i numeri e, se qualcuno di quelli che servirebbero è già
 * emesso, Python **apre una sfida** invece di decidere. Questo dialogo è quella
 * sfida: l'umano vede i codici in conflitto e sceglie.
 *
 * Tre cose che il dialogo non fa, deliberatamente:
 *
 * - **Non decide da sé se il processo è partito.** Mostra i codici e la conferma;
 *   l'esito lo dice `cf-operation-status`, che legge lo stato dell'operazione.
 * - **Non compone frasi sui codici.** `ORD-2026-001` è un dato: mostrarlo così
 *   com'è significa che l'utente lo ritrova identico nel registro e nell'audit.
 *   Una frase composta qui sarebbe una seconda traduzione.
 * - **Non si arrende a un doppio clic.** `canSubmit` viene dallo stato, non da
 *   un `disabled` locale: due conferme ravvicinate sono due consensi, e il
 *   secondo non è stato dato.
 */

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import React from "react";
import DialogHeader from "components/dialogs/DialogHeader";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import type { OperationStatus } from "model/CustomFields";
import { canConfirm, type StartOutcome } from "./startFlow";
import { messageKeyFor } from "./errorMessages";

interface DuplicateCodeDialogProps {
  open: boolean;
  /** L'esito corrente: è lui a dire se si può ancora inviare. */
  outcome: StartOutcome;
  /** I codici già usati, come li ha restituiti il server. */
  codes: readonly string[];
  /** Quante altre anomalie accompagnano il conflitto. */
  anomalies?: number;
  /**
   * Lo stato grezzo dell'operazione, quando il server ne ha dichiarato uno.
   *
   * Si mostra così com'è: è l'unica cosa che dice "applied" senza che
   * l'interfaccia lo deduca da una frase tradotta.
   */
  status?: OperationStatus | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const DuplicateCodeDialog: React.FC<DuplicateCodeDialogProps> = ({
  open,
  outcome,
  codes,
  anomalies = 0,
  status = null,
  onConfirm,
  onCancel,
}) => {
  const translations = useTranslations();
  const waiting = outcome.kind === "working";
  /**
   * E' partito davvero.
   *
   * In quello stato non c'e' piu' niente da decidere: si mostra l'esito e si
   * lascia chiudere. Un tasto "Annulla" suggerirebbe che l'avvio si possa
   * disfare, e non si puo'.
   */
  const started = outcome.kind === "started";

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      data-testid="cf-duplicate-dialog"
      id="duplicate-code-dialog"
    >
      <DialogHeader sx={{ px: 4 }} onClose={onCancel}>
        {getTranslation(translations, "custom_fields.sequence.duplicate.title")}
      </DialogHeader>
      <DialogContent>
        <Stack gap={2} pt={1}>
          <Typography variant="body2">
            {getTranslation(
              translations,
              "custom_fields.sequence.duplicate.message"
            )}
          </Typography>

          {/* I codici come sono: l'utente li ritrova identici altrove. */}
          <Stack gap={0.5} data-testid="cf-duplicate-codes">
            {codes.map((code) => (
              <Typography key={code} variant="body2" fontWeight="bold">
                {code}
              </Typography>
            ))}
          </Stack>

          {anomalies > 0 && (
            // L'insieme delle anomalie si mostra **intero**: confermare una per
            // volta su un insieme che cambia è come non averlo visto.
            <Typography variant="caption" color="text.secondary">
              {getTranslation(translations, "custom_fields.impact.others")}
              {": "}
              {anomalies}
            </Typography>
          )}

          {outcome.kind === "refused" && (
            <Alert severity="error" data-testid="cf-duplicate-refused">
              {getTranslation(translations, messageKeyFor({ code: outcome.code }))}
            </Alert>
          )}

          {/* Lo stato grezzo: "applied" si legge dal server, non da una frase
              che l'interfaccia ha composto. */}
          {status && <OperationStatusLine status={status} />}
        </Stack>
      </DialogContent>
      {/* A operazione avviata non resta niente da decidere: si chiude dal
          titolo. Gli altri stati hanno bisogno di una scelta. */}
      {!started && (
        <DialogActions>
          <Button onClick={onCancel} data-testid="cf-duplicate-cancel">
            {getTranslation(translations, "custom_fields.action.cancel")}
          </Button>
          <LoadingButton
            loading={waiting}
            // `canConfirm` e non un `disabled` locale: la guardia sta nello
            // stato dell'operazione, che è l'unica cosa che sa se una conferma è
            // già partita. Non `canSubmit`, che risponde all'altra domanda — se
            // si possa **avviare** — e in una sfida aperta dice di no.
            disabled={!canConfirm(outcome)}
            onClick={onConfirm}
            data-testid="cf-duplicate-confirm"
          >
            {getTranslation(translations, "custom_fields.sequence.duplicate.confirm")}
          </LoadingButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

/**
 * Lo stato dell'operazione, visibile all'utente.
 *
 * Esiste per una ragione sola: rendere impossibile dire "avviato" senza che lo
 * stato lo dica. Il `data-status` è lo stato grezzo, così un test può asserire
 * su `applied` invece che su un testo tradotto che cambierebbe.
 */
export const OperationStatusLine: React.FC<{ status: OperationStatus }> = ({
  status,
}) => {
  const translations = useTranslations();

  return (
    <Typography
      variant="caption"
      color="text.secondary"
      data-testid="cf-operation-status"
      data-status={status}
    >
      {getTranslation(translations, `custom_fields.operation.status.${status}`)}
    </Typography>
  );
};

export default DuplicateCodeDialog;
